import { Injectable } from '@angular/core';
import { FormBuilder, FormGroup, Validators, FormControl, AbstractControl, FormArray } from '@angular/forms';
import { NetzTranslateService } from 'src/app/services/netz-translate.service';
import { DrrCcdTypes, DeviceUnitFormModel, RegistrationUnitFormModel, MainsInterferenceDeviceTypes } from '../technical-data.domain';
import { createEmptyFileInputFormValue } from 'src/app/+custom-controls/components/file-input-form-control/file-input-form-control.component';

@Injectable()
export class DrrCcdDeviceControlsService {
  constructor(private fb: FormBuilder, private translation: NetzTranslateService) {}

  private unitPowerFieldNames = {
    electricalPower: 'electricalPower',
    powerOfAdditionalHeater: 'powerOfAdditionalHeater',
  };

  private subUnitPowerFieldNames = {
    electricalPower: 'electricalPower',
    powerOfAdditionalHeater: 'powerOfAdditionalHeater',
  };

  private _ccdType: string;
  get ccdType(): string {
    return this._ccdType;
  }
  set ccdType(value: string) {
    this._ccdType = value;
  }

  get networkDisturbancesDataControls() {
    return this.unitControls.registrationUnit?.controls?.networkDisturbancesData?.controls;
  }

  unitControls = {
    id: {
      add: form => form.addControl('id', this.fb.control(null)),
      visible: false,
      toggle: _ => {},
    },
    registrationUnit: {
      visible: false,
      reset: (form: FormGroup) => {
        this.resetUnitControls(this.unitControls.registrationUnit.controls, form.controls.registrationUnit as FormGroup);
      },
      add: (form: FormGroup) => {
        form.addControl('registrationUnit', this.fb.group({ id: null }));
        this.addAllControls(this.unitControls.registrationUnit.controls, form.controls.registrationUnit as FormGroup);
      },
      toggle: (form: FormGroup, value: DeviceUnitFormModel) => {
        this.unitControls.registrationUnit.visible = true;
        this.toggleAllControls(
          this.unitControls.registrationUnit.controls,
          form.controls.registrationUnit as FormGroup,
          value?.registrationUnit
        );
      },
      controls: {
        skMeasuringConcept: {
          visible: false,
          reset: form => form.controls.skMeasuringConcept.setValue(null),
          add: form => form.addControl('skMeasuringConcept', this.fb.control(null, [Validators.required])),
          toggle: (form: FormGroup, value: RegistrationUnitFormModel) => {
            this.unitControls.registrationUnit.controls.skMeasuringConcept.visible = [
              DrrCcdTypes.heating,
              DrrCcdTypes.singleDevice,
              DrrCcdTypes.energyManagement,
              DrrCcdTypes.emergencyGenerator,
            ].includes(this.ccdType);
            this.disableIfHidden(this.unitControls.registrationUnit.controls.skMeasuringConcept, form.controls.skMeasuringConcept);
            if (this.unitControls.registrationUnit.controls.skMeasuringConcept.visible) {
              this.toggleUnavailable(form.controls.skMeasuringConcept as FormControl, false);
            }
          },
        },
        deviceName: {
          visible: false,
          reset: form => form.controls.deviceName.setValue(null),
          add: form => form.addControl('deviceName', this.fb.control(null, [Validators.required])),
          toggle: (form: FormGroup, value: RegistrationUnitFormModel) => {
            this.unitControls.registrationUnit.controls.deviceName.visible = [
              DrrCcdTypes.heating,
              DrrCcdTypes.singleDevice,
              DrrCcdTypes.energyManagement,
              DrrCcdTypes.emergencyGenerator,
              DrrCcdTypes.mainsInterference,
              DrrCcdTypes.other,
            ].includes(this.ccdType);
            this.disableIfHidden(this.unitControls.registrationUnit.controls.deviceName, form.controls.deviceName);
            if (this.unitControls.registrationUnit.controls.deviceName.visible) {
              this.toggleUnavailable(form.controls.deviceName as FormControl, false);
            }
          },
        },
        heatingType: {
          visible: false,
          reset: form => form.controls.heatingType.setValue(null),
          add: form => form.addControl('heatingType', this.fb.control(null, [Validators.required])),
          toggle: (form: FormGroup, value: RegistrationUnitFormModel) => {
            this.unitControls.registrationUnit.controls.heatingType.visible = [DrrCcdTypes.heating].includes(this.ccdType);
            this.disableIfHidden(this.unitControls.registrationUnit.controls.heatingType, form.controls.heatingType);
            if (this.unitControls.registrationUnit.controls.heatingType.visible) {
              this.toggleUnavailable(form.controls.heatingType as FormControl, false);
            }
          },
        },
        deviceNumber: {
          visible: false,
          reset: form => form.controls.deviceNumber.setValue(null),
          add: form => form.addControl('deviceNumber', this.fb.control(null, [Validators.required])),
          toggle: (form: FormGroup, value: RegistrationUnitFormModel) => {
            this.unitControls.registrationUnit.controls.deviceNumber.visible = [
              DrrCcdTypes.heating,
              DrrCcdTypes.singleDevice,
              DrrCcdTypes.energyManagement,
              DrrCcdTypes.emergencyGenerator,
              DrrCcdTypes.mainsInterference,
            ].includes(this.ccdType);
            this.disableIfHidden(this.unitControls.registrationUnit.controls.deviceNumber, form.controls.deviceNumber);
            if (this.unitControls.registrationUnit.controls.deviceNumber.visible) {
              this.toggleUnavailable(form.controls.deviceNumber as FormControl, false);
            }
          },
        },
        isSystemInstallationVdeArn4100: {
          visible: false,
          reset: form => form.controls.isSystemInstallationVdeArn4100.setValue(null),
          add: form => form.addControl('isSystemInstallationVdeArn4100', this.fb.control(null)),
          toggle: (form: FormGroup, value: RegistrationUnitFormModel) => {
            this.unitControls.registrationUnit.controls.isSystemInstallationVdeArn4100.visible = [DrrCcdTypes.energyManagement].includes(
              this.ccdType
            );
            this.disableIfHidden(
              this.unitControls.registrationUnit.controls.isSystemInstallationVdeArn4100,
              form.controls.isSystemInstallationVdeArn4100
            );
            if (this.unitControls.registrationUnit.controls.isSystemInstallationVdeArn4100.visible) {
              this.toggleUnavailable(form.controls.isSystemInstallationVdeArn4100 as FormControl, false);
            }
          },
        },
        isPowerProvidedFromCustomerSystem: {
          visible: false,
          reset: form => form.controls.isPowerProvidedFromCustomerSystem.setValue(null),
          add: form => form.addControl('isPowerProvidedFromCustomerSystem', this.fb.control(null)),
          toggle: (form: FormGroup, value: RegistrationUnitFormModel) => {
            this.unitControls.registrationUnit.controls.isPowerProvidedFromCustomerSystem.visible = [DrrCcdTypes.energyManagement].includes(
              this.ccdType
            );
            this.disableIfHidden(
              this.unitControls.registrationUnit.controls.isPowerProvidedFromCustomerSystem,
              form.controls.isPowerProvidedFromCustomerSystem
            );
            if (this.unitControls.registrationUnit.controls.isPowerProvidedFromCustomerSystem.visible) {
              this.toggleUnavailable(form.controls.isPowerProvidedFromCustomerSystem as FormControl, false);
            }
          },
        },
        isEmergencyGeneratorNotInParallel: {
          visible: false,
          reset: form => form.controls.isEmergencyGeneratorNotInParallel.setValue(null),
          add: form => form.addControl('isEmergencyGeneratorNotInParallel', this.fb.control(null)),
          toggle: (form: FormGroup, value: RegistrationUnitFormModel) => {
            this.unitControls.registrationUnit.controls.isEmergencyGeneratorNotInParallel.visible = [
              DrrCcdTypes.emergencyGenerator,
            ].includes(this.ccdType);
            this.disableIfHidden(
              this.unitControls.registrationUnit.controls.isEmergencyGeneratorNotInParallel,
              form.controls.isEmergencyGeneratorNotInParallel
            );
            if (this.unitControls.registrationUnit.controls.isEmergencyGeneratorNotInParallel.visible) {
              this.toggleUnavailable(form.controls.isEmergencyGeneratorNotInParallel as FormControl, false);
            }
          },
        },
        isEnergyFromEmergencyGeneratorNotRemunerated: {
          visible: false,
          reset: form => form.controls.isEnergyFromEmergencyGeneratorNotRemunerated.setValue(null),
          add: form => form.addControl('isEnergyFromEmergencyGeneratorNotRemunerated', this.fb.control(null)),
          toggle: (form: FormGroup, value: RegistrationUnitFormModel) => {
            this.unitControls.registrationUnit.controls.isEnergyFromEmergencyGeneratorNotRemunerated.visible = [
              DrrCcdTypes.emergencyGenerator,
            ].includes(this.ccdType);
            this.disableIfHidden(
              this.unitControls.registrationUnit.controls.isEnergyFromEmergencyGeneratorNotRemunerated,
              form.controls.isEnergyFromEmergencyGeneratorNotRemunerated
            );
            if (this.unitControls.registrationUnit.controls.isEnergyFromEmergencyGeneratorNotRemunerated.visible) {
              this.toggleUnavailable(form.controls.isEnergyFromEmergencyGeneratorNotRemunerated as FormControl, false);
            }
          },
        },
        singleDeviceType: {
          visible: false,
          reset: form => form.controls.singleDeviceType.setValue(null),
          add: form => form.addControl('singleDeviceType', this.fb.control(null, [Validators.required])),
          toggle: (form: FormGroup, value: RegistrationUnitFormModel) => {
            this.unitControls.registrationUnit.controls.singleDeviceType.visible = [DrrCcdTypes.singleDevice].includes(this.ccdType);
            this.disableIfHidden(this.unitControls.registrationUnit.controls.singleDeviceType, form.controls.singleDeviceType);
            if (this.unitControls.registrationUnit.controls.singleDeviceType.visible) {
              this.toggleUnavailable(form.controls.singleDeviceType as FormControl, false);
            }
          },
        },
        mainsInterferenceDeviceType: {
          visible: false,
          reset: form => form.controls.mainsInterferenceDeviceType.setValue(null),
          add: form => form.addControl('mainsInterferenceDeviceType', this.fb.control(null, [Validators.required])),
          toggle: (form: FormGroup, value: RegistrationUnitFormModel) => {
            this.unitControls.registrationUnit.controls.mainsInterferenceDeviceType.visible = [DrrCcdTypes.mainsInterference].includes(
              this.ccdType
            );
            this.disableIfHidden(
              this.unitControls.registrationUnit.controls.mainsInterferenceDeviceType,
              form.controls.mainsInterferenceDeviceType
            );
            if (this.unitControls.registrationUnit.controls.mainsInterferenceDeviceType.visible) {
              this.toggleUnavailable(form.controls.mainsInterferenceDeviceType as FormControl, false);
            }
          },
        },
        connectionType: {
          visible: false,
          reset: form => form.controls.connectionType.setValue(null),
          add: form => form.addControl('connectionType', this.fb.control(null, [Validators.required])),
          toggle: (form: FormGroup, value: RegistrationUnitFormModel) => {
            this.unitControls.registrationUnit.controls.connectionType.visible = [DrrCcdTypes.mainsInterference].includes(this.ccdType);
            this.disableIfHidden(this.unitControls.registrationUnit.controls.connectionType, form.controls.connectionType);
            if (this.unitControls.registrationUnit.controls.connectionType.visible) {
              this.toggleUnavailable(form.controls.connectionType as FormControl, false);
            }
          },
        },
        isNetworkDisturbanceCompliedWithVdeArn4100: {
          visible: false,
          reset: form => form.controls.isNetworkDisturbanceCompliedWithVdeArn4100.setValue(null),
          add: form => form.addControl('isNetworkDisturbanceCompliedWithVdeArn4100', this.fb.control(null, [Validators.required])),
          toggle: (form: FormGroup, value: RegistrationUnitFormModel) => {
            this.unitControls.registrationUnit.controls.isNetworkDisturbanceCompliedWithVdeArn4100.visible = [
              DrrCcdTypes.mainsInterference,
            ].includes(this.ccdType);
            this.disableIfHidden(
              this.unitControls.registrationUnit.controls.isNetworkDisturbanceCompliedWithVdeArn4100,
              form.controls.isNetworkDisturbanceCompliedWithVdeArn4100
            );
            if (this.unitControls.registrationUnit.controls.isNetworkDisturbanceCompliedWithVdeArn4100.visible) {
              this.toggleUnavailable(form.controls.isNetworkDisturbanceCompliedWithVdeArn4100 as FormControl, false);
            }
          },
        },
        isInputOfCustomerSystemLessOrEqual75: {
          visible: false,
          reset: form => form.controls.isInputOfCustomerSystemLessOrEqual75.setValue(null),
          add: form => form.addControl('isInputOfCustomerSystemLessOrEqual75', this.fb.control(null, [Validators.required])),
          toggle: (form: FormGroup, value: RegistrationUnitFormModel) => {
            this.unitControls.registrationUnit.controls.isInputOfCustomerSystemLessOrEqual75.visible = [
              DrrCcdTypes.mainsInterference,
            ].includes(this.ccdType);
            this.disableIfHidden(
              this.unitControls.registrationUnit.controls.isInputOfCustomerSystemLessOrEqual75,
              form.controls.isInputOfCustomerSystemLessOrEqual75
            );
            if (this.unitControls.registrationUnit.controls.isInputOfCustomerSystemLessOrEqual75.visible) {
              this.toggleUnavailable(form.controls.isInputOfCustomerSystemLessOrEqual75 as FormControl, false);
            }
          },
        },
        networkDisturbancesData: {
          visible: false,
          reset: (form: FormGroup) => {
            this.resetUnitControls(
              this.unitControls.registrationUnit.controls.networkDisturbancesData.controls,
              form.controls.networkDisturbancesData as FormGroup
            );
          },
          add: (form: FormGroup) => {
            form.addControl('networkDisturbancesData', this.fb.group({ id: null }));
            this.addAllControls(this.networkDisturbancesDataControls, form.controls.networkDisturbancesData as FormGroup);
          },
          toggle: (form: FormGroup, value: RegistrationUnitFormModel) => {
            this.unitControls.registrationUnit.controls.networkDisturbancesData.visible = true;
            this.toggleAllControls(this.networkDisturbancesDataControls, form.controls.networkDisturbancesData as FormGroup, value);
          },
          controls: {
            isAsynchronousMotor: {
              visible: false,
              reset: form => form.controls.isAsynchronousMotor.setValue(null),
              add: form => form.addControl('isAsynchronousMotor', this.fb.control(null)),
              toggle: (form: FormGroup, value: any) => {
                this.networkDisturbancesDataControls.isAsynchronousMotor.visible =
                  value?.mainsInterferenceDeviceType === MainsInterferenceDeviceTypes.motors;
                this.disableIfHidden(this.networkDisturbancesDataControls.isAsynchronousMotor, form.controls.isAsynchronousMotor);
                if (this.networkDisturbancesDataControls.isAsynchronousMotor.visible) {
                  this.toggleUnavailable(form.controls.isAsynchronousMotor as FormControl, false);
                }
              },
            },
            isDriveWithPowerConverter: {
              visible: false,
              reset: form => form.controls.isDriveWithPowerConverter.setValue(null),
              add: form => form.addControl('isDriveWithPowerConverter', this.fb.control(null)),
              toggle: (form: FormGroup, value: any) => {
                this.networkDisturbancesDataControls.isDriveWithPowerConverter.visible =
                  value?.mainsInterferenceDeviceType === MainsInterferenceDeviceTypes.motors;
                this.disableIfHidden(
                  this.networkDisturbancesDataControls.isDriveWithPowerConverter,
                  form.controls.isDriveWithPowerConverter
                );
                if (this.networkDisturbancesDataControls.isDriveWithPowerConverter.visible) {
                  this.toggleUnavailable(form.controls.isDriveWithPowerConverter as FormControl, false);
                }
              },
            },
            ratedPower: {
              visible: false,
              reset: form => form.controls.ratedPower.setValue(null),
              label: value =>
                value === MainsInterferenceDeviceTypes.transformers
                  ? this.translation.translateString('drr-ccd-devices.transformer-rated-power-lbl')
                  : this.translation.translateString('drr-ccd-devices.rated-power-lbl'),
              suffix: value => (value === MainsInterferenceDeviceTypes.transformers ? 'kVA' : 'KW'),
              add: form => form.addControl('ratedPower', this.fb.control(null, [Validators.required])),
              toggle: (form: FormGroup, value: any) => {
                this.networkDisturbancesDataControls.ratedPower.visible = [
                  MainsInterferenceDeviceTypes.motors,
                  MainsInterferenceDeviceTypes.applianceWithConverters,
                  MainsInterferenceDeviceTypes.transformers,
                ].includes(value?.mainsInterferenceDeviceType);
                this.disableIfHidden(this.networkDisturbancesDataControls.ratedPower, form.controls.ratedPower);
                if (this.networkDisturbancesDataControls.ratedPower.visible) {
                  this.toggleUnavailable(form.controls.ratedPower as FormControl, false);
                }
              },
            },
            ratedVoltage: {
              visible: false,
              reset: form => form.controls.ratedVoltage.setValue(null),
              add: form => form.addControl('ratedVoltage', this.fb.control(null, [Validators.required])),
              toggle: (form: FormGroup, value: any) => {
                this.networkDisturbancesDataControls.ratedVoltage.visible =
                  value?.mainsInterferenceDeviceType === MainsInterferenceDeviceTypes.motors;
                this.disableIfHidden(this.networkDisturbancesDataControls.ratedVoltage, form.controls.ratedVoltage);
                if (this.networkDisturbancesDataControls.ratedVoltage.visible) {
                  this.toggleUnavailable(form.controls.ratedVoltage as FormControl, false);
                }
              },
            },
            ratedSpeed: {
              visible: false,
              reset: form => form.controls.ratedSpeed.setValue(null),
              add: form => form.addControl('ratedSpeed', this.fb.control(null, [Validators.required])),
              toggle: (form: FormGroup, value: any) => {
                this.networkDisturbancesDataControls.ratedSpeed.visible =
                  value?.mainsInterferenceDeviceType === MainsInterferenceDeviceTypes.motors;
                this.disableIfHidden(this.networkDisturbancesDataControls.ratedSpeed, form.controls.ratedSpeed);
                if (this.networkDisturbancesDataControls.ratedSpeed.visible) {
                  this.toggleUnavailable(form.controls.ratedSpeed as FormControl, false);
                }
              },
            },
            ratedCurrent: {
              visible: false,
              reset: form => form.controls.ratedCurrent.setValue(null),
              add: form => form.addControl('ratedCurrent', this.fb.control(null, [Validators.required])),
              toggle: (form: FormGroup, value: any) => {
                this.networkDisturbancesDataControls.ratedCurrent.visible =
                  value?.mainsInterferenceDeviceType === MainsInterferenceDeviceTypes.motors;
                this.disableIfHidden(this.networkDisturbancesDataControls.ratedCurrent, form.controls.ratedCurrent);
                if (this.networkDisturbancesDataControls.ratedCurrent.visible) {
                  this.toggleUnavailable(form.controls.ratedCurrent as FormControl, false);
                }
              },
            },
            powerFactor: {
              visible: false,
              reset: form => form.controls.powerFactor.setValue(null),
              add: form => form.addControl('powerFactor', this.fb.control(null, [Validators.required])),
              toggle: (form: FormGroup, value: any) => {
                this.networkDisturbancesDataControls.powerFactor.visible = [
                  MainsInterferenceDeviceTypes.motors,
                  MainsInterferenceDeviceTypes.weldingMachines,
                ].includes(value?.mainsInterferenceDeviceType);
                this.disableIfHidden(this.networkDisturbancesDataControls.powerFactor, form.controls.powerFactor);
                if (this.networkDisturbancesDataControls.powerFactor.visible) {
                  this.toggleUnavailable(form.controls.powerFactor as FormControl, false);
                }
              },
            },
            efficiency: {
              visible: false,
              reset: form => form.controls.efficiency.setValue(null),
              add: form => form.addControl('efficiency', this.fb.control(null, [Validators.required])),
              toggle: (form: FormGroup, value: any) => {
                this.networkDisturbancesDataControls.efficiency.visible =
                  value?.mainsInterferenceDeviceType === MainsInterferenceDeviceTypes.motors;
                this.disableIfHidden(this.networkDisturbancesDataControls.efficiency, form.controls.efficiency);
                if (this.networkDisturbancesDataControls.efficiency.visible) {
                  this.toggleUnavailable(form.controls.efficiency as FormControl, false);
                }
              },
            },
            ratioStartingCurrentRated: {
              visible: false,
              reset: form => form.controls.ratioStartingCurrentRated.setValue(null),
              add: form => form.addControl('ratioStartingCurrentRated', this.fb.control(null, [Validators.required])),
              toggle: (form: FormGroup, value: any) => {
                this.networkDisturbancesDataControls.ratioStartingCurrentRated.visible =
                  value?.mainsInterferenceDeviceType === MainsInterferenceDeviceTypes.motors;
                this.disableIfHidden(
                  this.networkDisturbancesDataControls.ratioStartingCurrentRated,
                  form.controls.ratioStartingCurrentRated
                );
                if (this.networkDisturbancesDataControls.ratioStartingCurrentRated.visible) {
                  this.toggleUnavailable(form.controls.ratioStartingCurrentRated as FormControl, false);
                }
              },
            },
            startUpType: {
              visible: false,
              reset: form => form.controls.startUpType.setValue(null),
              add: form => form.addControl('startUpType', this.fb.control(null, [Validators.required])),
              toggle: (form: FormGroup, value: any) => {
                this.networkDisturbancesDataControls.startUpType.visible =
                  value?.mainsInterferenceDeviceType === MainsInterferenceDeviceTypes.motors;
                this.disableIfHidden(this.networkDisturbancesDataControls.startUpType, form.controls.startUpType);
                if (this.networkDisturbancesDataControls.startUpType.visible) {
                  this.toggleUnavailable(form.controls.startUpType as FormControl, false);
                }
              },
            },
            startUpCircuit: {
              visible: false,
              reset: form => form.controls.startUpCircuit.setValue(null),
              add: form => form.addControl('startUpCircuit', this.fb.control(null, [Validators.required])),
              toggle: (form: FormGroup, value: any) => {
                this.networkDisturbancesDataControls.startUpCircuit.visible =
                  value?.mainsInterferenceDeviceType === MainsInterferenceDeviceTypes.motors;
                this.disableIfHidden(this.networkDisturbancesDataControls.startUpCircuit, form.controls.startUpCircuit);
                if (this.networkDisturbancesDataControls.startUpCircuit.visible) {
                  this.toggleUnavailable(form.controls.startUpCircuit as FormControl, false);
                }
              },
            },
            otherStartupCircuit: {
              visible: false,
              reset: form => form.controls.otherStartupCircuit.setValue(null),
              add: form => form.addControl('otherStartupCircuit', this.fb.control(null)),
              toggle: (form: FormGroup, value: any) => {
                this.networkDisturbancesDataControls.otherStartupCircuit.visible =
                  value?.mainsInterferenceDeviceType === MainsInterferenceDeviceTypes.motors && value?.startUpCircuit === 'other';
                this.disableIfHidden(this.networkDisturbancesDataControls.otherStartupCircuit, form.controls.otherStartupCircuit);
                if (this.networkDisturbancesDataControls.otherStartupCircuit.visible) {
                  this.toggleUnavailable(form.controls.otherStartupCircuit as FormControl, false);
                }
              },
            },
            loadNumberRotationChanges: {
              visible: false,
              reset: form => form.controls.loadNumberRotationChanges.setValue(null),
              add: form => form.addControl('loadNumberRotationChanges', this.fb.control(null, [Validators.required])),
              toggle: (form: FormGroup, value: any) => {
                this.networkDisturbancesDataControls.loadNumberRotationChanges.visible =
                  value?.mainsInterferenceDeviceType === MainsInterferenceDeviceTypes.motors;
                this.disableIfHidden(
                  this.networkDisturbancesDataControls.loadNumberRotationChanges,
                  form.controls.loadNumberRotationChanges
                );
                if (this.networkDisturbancesDataControls.loadNumberRotationChanges.visible) {
                  this.toggleUnavailable(form.controls.loadNumberRotationChanges as FormControl, false);
                }
              },
            },
            attemptsPerHour: {
              visible: false,
              reset: form => form.controls.attemptsPerHour.setValue(null),
              add: form => form.addControl('attemptsPerHour', this.fb.control(null, [Validators.required])),
              toggle: (form: FormGroup, value: any) => {
                this.networkDisturbancesDataControls.attemptsPerHour.visible =
                  value?.mainsInterferenceDeviceType === MainsInterferenceDeviceTypes.motors;
                this.disableIfHidden(this.networkDisturbancesDataControls.attemptsPerHour, form.controls.attemptsPerHour);
                if (this.networkDisturbancesDataControls.attemptsPerHour.visible) {
                  this.toggleUnavailable(form.controls.attemptsPerHour as FormControl, false);
                }
              },
            },
            attemptsPerDay: {
              visible: false,
              reset: form => form.controls.attemptsPerDay.setValue(null),
              add: form => form.addControl('attemptsPerDay', this.fb.control(null, [Validators.required])),
              toggle: (form: FormGroup, value: any) => {
                this.networkDisturbancesDataControls.attemptsPerDay.visible =
                  value?.mainsInterferenceDeviceType === MainsInterferenceDeviceTypes.motors;
                this.disableIfHidden(this.networkDisturbancesDataControls.attemptsPerDay, form.controls.attemptsPerDay);
                if (this.networkDisturbancesDataControls.attemptsPerDay.visible) {
                  this.toggleUnavailable(form.controls.attemptsPerDay as FormControl, false);
                }
              },
            },
            maxWeldingPerformance: {
              visible: false,
              reset: form => form.controls.maxWeldingPerformance.setValue(null),
              add: form => form.addControl('maxWeldingPerformance', this.fb.control(null, [Validators.required])),
              toggle: (form: FormGroup, value: any) => {
                this.networkDisturbancesDataControls.maxWeldingPerformance.visible =
                  value?.mainsInterferenceDeviceType === MainsInterferenceDeviceTypes.weldingMachines;
                this.disableIfHidden(this.networkDisturbancesDataControls.maxWeldingPerformance, form.controls.maxWeldingPerformance);
                if (this.networkDisturbancesDataControls.maxWeldingPerformance.visible) {
                  this.toggleUnavailable(form.controls.maxWeldingPerformance as FormControl, false);
                }
              },
            },
            weldNumber: {
              visible: false,
              reset: form => form.controls.weldNumber.setValue(null),
              add: form => form.addControl('weldNumber', this.fb.control(null, [Validators.required])),
              toggle: (form: FormGroup, value: any) => {
                this.networkDisturbancesDataControls.weldNumber.visible =
                  value?.mainsInterferenceDeviceType === MainsInterferenceDeviceTypes.weldingMachines;
                this.disableIfHidden(this.networkDisturbancesDataControls.weldNumber, form.controls.weldNumber);
                if (this.networkDisturbancesDataControls.weldNumber.visible) {
                  this.toggleUnavailable(form.controls.weldNumber as FormControl, false);
                }
              },
            },
            weldDuration: {
              visible: false,
              reset: form => form.controls.weldDuration.setValue(null),
              add: form => form.addControl('weldDuration', this.fb.control(null, [Validators.required])),
              toggle: (form: FormGroup, value: any) => {
                this.networkDisturbancesDataControls.weldDuration.visible =
                  value?.mainsInterferenceDeviceType === MainsInterferenceDeviceTypes.weldingMachines;
                this.disableIfHidden(this.networkDisturbancesDataControls.weldDuration, form.controls.weldDuration);
                if (this.networkDisturbancesDataControls.weldDuration.visible) {
                  this.toggleUnavailable(form.controls.weldDuration as FormControl, false);
                }
              },
            },
            xrayRatedPower: {
              visible: false,
              reset: form => form.controls.xrayRatedPower.setValue(null),
              add: form => form.addControl('xrayRatedPower', this.fb.control(null, [Validators.required])),
              toggle: (form: FormGroup, value: any) => {
                this.networkDisturbancesDataControls.xrayRatedPower.visible =
                  value?.mainsInterferenceDeviceType === MainsInterferenceDeviceTypes.xrayMachines;
                this.disableIfHidden(this.networkDisturbancesDataControls.xrayRatedPower, form.controls.xrayRatedPower);
                if (this.networkDisturbancesDataControls.xrayRatedPower.visible) {
                  this.toggleUnavailable(form.controls.xrayRatedPower as FormControl, false);
                }
              },
            },
            xrayRequiredPower: {
              visible: false,
              reset: form => form.controls.xrayRequiredPower.setValue(null),
              add: form => form.addControl('xrayRequiredPower', this.fb.control(null, [Validators.required])),
              toggle: (form: FormGroup, value: any) => {
                this.networkDisturbancesDataControls.xrayRequiredPower.visible =
                  value?.mainsInterferenceDeviceType === MainsInterferenceDeviceTypes.xrayMachines;
                this.disableIfHidden(this.networkDisturbancesDataControls.xrayRequiredPower, form.controls.xrayRequiredPower);
                if (this.networkDisturbancesDataControls.xrayRequiredPower.visible) {
                  this.toggleUnavailable(form.controls.xrayRequiredPower as FormControl, false);
                }
              },
            },
            powerConverterEfficiency: {
              visible: false,
              reset: form => form.controls.powerConverterEfficiency.setValue(null),
              add: form => form.addControl('powerConverterEfficiency', this.fb.control(null, [Validators.required])),
              toggle: (form: FormGroup, value: any) => {
                this.networkDisturbancesDataControls.powerConverterEfficiency.visible =
                  value?.mainsInterferenceDeviceType === MainsInterferenceDeviceTypes.xrayMachines;
                this.disableIfHidden(this.networkDisturbancesDataControls.powerConverterEfficiency, form.controls.powerConverterEfficiency);
                if (this.networkDisturbancesDataControls.powerConverterEfficiency.visible) {
                  this.toggleUnavailable(form.controls.powerConverterEfficiency as FormControl, false);
                }
              },
            },
            maxNumberOfRecordings: {
              visible: false,
              reset: form => form.controls.maxNumberOfRecordings.setValue(null),
              add: form => form.addControl('maxNumberOfRecordings', this.fb.control(null, [Validators.required])),
              toggle: (form: FormGroup, value: any) => {
                this.networkDisturbancesDataControls.maxNumberOfRecordings.visible =
                  value?.mainsInterferenceDeviceType === MainsInterferenceDeviceTypes.xrayMachines;
                this.disableIfHidden(this.networkDisturbancesDataControls.maxNumberOfRecordings, form.controls.maxNumberOfRecordings);
                if (this.networkDisturbancesDataControls.maxNumberOfRecordings.visible) {
                  this.toggleUnavailable(form.controls.maxNumberOfRecordings as FormControl, false);
                }
              },
            },
            commutationInductance: {
              visible: false,
              reset: form => form.controls.commutationInductance.setValue(null),
              add: form => form.addControl('commutationInductance', this.fb.control(null, [Validators.required])),
              toggle: (form: FormGroup, value: any) => {
                this.networkDisturbancesDataControls.commutationInductance.visible =
                  value?.mainsInterferenceDeviceType === MainsInterferenceDeviceTypes.applianceWithConverters;
                this.disableIfHidden(this.networkDisturbancesDataControls.commutationInductance, form.controls.commutationInductance);
                if (this.networkDisturbancesDataControls.commutationInductance.visible) {
                  this.toggleUnavailable(form.controls.commutationInductance as FormControl, false);
                }
              },
            },
            converterTransformer: {
              visible: false,
              reset: form => form.controls.converterTransformer.setValue(null),
              add: form => form.addControl('converterTransformer', this.fb.control(null, [Validators.required])),
              toggle: (form: FormGroup, value: any) => {
                this.networkDisturbancesDataControls.converterTransformer.visible =
                  value?.mainsInterferenceDeviceType === MainsInterferenceDeviceTypes.applianceWithConverters;
                this.disableIfHidden(this.networkDisturbancesDataControls.converterTransformer, form.controls.converterTransformer);
                if (this.networkDisturbancesDataControls.converterTransformer.visible) {
                  this.toggleUnavailable(form.controls.converterTransformer as FormControl, false);
                }
              },
            },
            rectifierDesign: {
              visible: false,
              reset: form => form.controls.rectifierDesign.setValue(null),
              add: form => form.addControl('rectifierDesign', this.fb.control(null, [Validators.required])),
              toggle: (form: FormGroup, value: any) => {
                this.networkDisturbancesDataControls.rectifierDesign.visible =
                  value?.mainsInterferenceDeviceType === MainsInterferenceDeviceTypes.applianceWithConverters;
                this.disableIfHidden(this.networkDisturbancesDataControls.rectifierDesign, form.controls.rectifierDesign);
                if (this.networkDisturbancesDataControls.rectifierDesign.visible) {
                  this.toggleUnavailable(form.controls.rectifierDesign as FormControl, false);
                }
              },
            },
            pulseNumber: {
              visible: false,
              reset: form => form.controls.pulseNumber.setValue(null),
              add: form => form.addControl('pulseNumber', this.fb.control(null, [Validators.required])),
              toggle: (form: FormGroup, value: any) => {
                this.networkDisturbancesDataControls.pulseNumber.visible =
                  value?.mainsInterferenceDeviceType === MainsInterferenceDeviceTypes.applianceWithConverters;
                this.disableIfHidden(this.networkDisturbancesDataControls.pulseNumber, form.controls.pulseNumber);
                if (this.networkDisturbancesDataControls.pulseNumber.visible) {
                  this.toggleUnavailable(form.controls.pulseNumber as FormControl, false);
                }
              },
            },
            switchingGroup: {
              visible: false,
              reset: form => form.controls.switchingGroup.setValue(null),
              add: form => form.addControl('switchingGroup', this.fb.control(null, [Validators.required])),
              toggle: (form: FormGroup, value: any) => {
                this.networkDisturbancesDataControls.switchingGroup.visible = [
                  MainsInterferenceDeviceTypes.applianceWithConverters,
                  MainsInterferenceDeviceTypes.transformers,
                ].includes(value?.mainsInterferenceDeviceType);
                this.disableIfHidden(this.networkDisturbancesDataControls.switchingGroup, form.controls.switchingGroup);
                if (this.networkDisturbancesDataControls.switchingGroup.visible) {
                  this.toggleUnavailable(form.controls.switchingGroup as FormControl, false);
                }
              },
            },
            powerConverterType: {
              visible: false,
              reset: form => form.controls.powerConverterType.setValue(null),
              add: form => form.addControl('powerConverterType', this.fb.control(null, [Validators.required])),
              toggle: (form: FormGroup, value: any) => {
                this.networkDisturbancesDataControls.powerConverterType.visible =
                  value?.mainsInterferenceDeviceType === MainsInterferenceDeviceTypes.applianceWithConverters;
                this.disableIfHidden(this.networkDisturbancesDataControls.powerConverterType, form.controls.powerConverterType);
                if (this.networkDisturbancesDataControls.powerConverterType.visible) {
                  this.toggleUnavailable(form.controls.powerConverterType as FormControl, false);
                }
              },
            },
            relativeVoltage: {
              visible: false,
              reset: form => form.controls.relativeVoltage.setValue(null),
              label: value =>
                value === MainsInterferenceDeviceTypes.transformers
                  ? this.translation.translateString('drr-ccd-devices.transformer-relative-voltage-lbl')
                  : this.translation.translateString('drr-ccd-devices.relative-voltage-lbl'),
              suffix: value => (value === MainsInterferenceDeviceTypes.transformers ? '%' : ''),
              add: form => form.addControl('relativeVoltage', this.fb.control(null, [Validators.required])),
              toggle: (form: FormGroup, value: any) => {
                this.networkDisturbancesDataControls.relativeVoltage.visible = [
                  MainsInterferenceDeviceTypes.applianceWithConverters,
                  MainsInterferenceDeviceTypes.transformers,
                ].includes(value?.mainsInterferenceDeviceType);
                this.disableIfHidden(this.networkDisturbancesDataControls.relativeVoltage, form.controls.relativeVoltage);
                if (this.networkDisturbancesDataControls.relativeVoltage.visible) {
                  this.toggleUnavailable(form.controls.relativeVoltage as FormControl, false);
                }
              },
            },
            centerPointCircuitType: {
              visible: false,
              reset: form => form.controls.centerPointCircuitType.setValue(null),
              add: form => form.addControl('centerPointCircuitType', this.fb.control(null, [Validators.required])),
              toggle: (form: FormGroup, value: any) => {
                this.networkDisturbancesDataControls.centerPointCircuitType.visible =
                  value?.mainsInterferenceDeviceType === MainsInterferenceDeviceTypes.applianceWithConverters;
                this.disableIfHidden(this.networkDisturbancesDataControls.centerPointCircuitType, form.controls.centerPointCircuitType);
                if (this.networkDisturbancesDataControls.centerPointCircuitType.visible) {
                  this.toggleUnavailable(form.controls.centerPointCircuitType as FormControl, false);
                }
              },
            },
            maxInrushCurrent: {
              visible: false,
              reset: form => form.controls.maxInrushCurrent.setValue(null),
              add: form => form.addControl('maxInrushCurrent', this.fb.control(null, [Validators.required])),
              toggle: (form: FormGroup, value: any) => {
                this.networkDisturbancesDataControls.maxInrushCurrent.visible =
                  value?.mainsInterferenceDeviceType === MainsInterferenceDeviceTypes.transformers;
                this.disableIfHidden(this.networkDisturbancesDataControls.maxInrushCurrent, form.controls.maxInrushCurrent);
                if (this.networkDisturbancesDataControls.maxInrushCurrent.visible) {
                  this.toggleUnavailable(form.controls.maxInrushCurrent as FormControl, false);
                }
              },
            },
            reactivePowerRange: {
              visible: false,
              reset: form => form.controls.reactivePowerRange.setValue(null),
              add: form => form.addControl('reactivePowerRange', this.fb.control(null, [Validators.required])),
              toggle: (form: FormGroup, value: any) => {
                this.networkDisturbancesDataControls.reactivePowerRange.visible =
                  value?.mainsInterferenceDeviceType === MainsInterferenceDeviceTypes.reactivePowerCompensationSystems;
                this.disableIfHidden(this.networkDisturbancesDataControls.reactivePowerRange, form.controls.reactivePowerRange);
                if (this.networkDisturbancesDataControls.reactivePowerRange.visible) {
                  this.toggleUnavailable(form.controls.reactivePowerRange as FormControl, false);
                }
              },
            },
            reactivePowerPerStage: {
              visible: false,
              reset: form => form.controls.reactivePowerPerStage.setValue(null),
              add: form => form.addControl('reactivePowerPerStage', this.fb.control(null, [Validators.required])),
              toggle: (form: FormGroup, value: any) => {
                this.networkDisturbancesDataControls.reactivePowerPerStage.visible =
                  value?.mainsInterferenceDeviceType === MainsInterferenceDeviceTypes.reactivePowerCompensationSystems;
                this.disableIfHidden(this.networkDisturbancesDataControls.reactivePowerPerStage, form.controls.reactivePowerPerStage);
                if (this.networkDisturbancesDataControls.reactivePowerPerStage.visible) {
                  this.toggleUnavailable(form.controls.reactivePowerPerStage as FormControl, false);
                }
              },
            },
            numberOfSteps: {
              visible: false,
              reset: form => form.controls.numberOfSteps.setValue(null),
              add: form => form.addControl('numberOfSteps', this.fb.control(null, [Validators.required])),
              toggle: (form: FormGroup, value: any) => {
                this.networkDisturbancesDataControls.numberOfSteps.visible =
                  value?.mainsInterferenceDeviceType === MainsInterferenceDeviceTypes.reactivePowerCompensationSystems;
                this.disableIfHidden(this.networkDisturbancesDataControls.numberOfSteps, form.controls.numberOfSteps);
                if (this.networkDisturbancesDataControls.numberOfSteps.visible) {
                  this.toggleUnavailable(form.controls.numberOfSteps as FormControl, false);
                }
              },
            },
            resonanceFrequency: {
              visible: false,
              reset: form => form.controls.resonanceFrequency.setValue(null),
              add: form => form.addControl('resonanceFrequency', this.fb.control(null, [Validators.required])),
              toggle: (form: FormGroup, value: any) => {
                this.networkDisturbancesDataControls.resonanceFrequency.visible =
                  value?.mainsInterferenceDeviceType === MainsInterferenceDeviceTypes.reactivePowerCompensationSystems;
                this.disableIfHidden(this.networkDisturbancesDataControls.resonanceFrequency, form.controls.resonanceFrequency);
                if (this.networkDisturbancesDataControls.resonanceFrequency.visible) {
                  this.toggleUnavailable(form.controls.resonanceFrequency as FormControl, false);
                }
              },
            },
          },
        },
        _dataCollectionDocument: {
          add: _ => {},
          default: createEmptyFileInputFormValue('drr_ccd_data_collection'),
          visible: false,
          toggle: (form: FormGroup, value: RegistrationUnitFormModel) => {
            form.controls.dataCollectionDocument &&
              this.disableIfHidden(
                this.unitControls.registrationUnit.controls._dataCollectionDocument,
                form.controls.dataCollectionDocument
              );
          },
        },
        _sitePlanInstallationDocument: {
          add: _ => {},
          default: createEmptyFileInputFormValue('drr_ccd_site_plan_installation'),
          visible: false,
          toggle: (form: FormGroup, value: RegistrationUnitFormModel) => {
            form.controls.sitePlanInstallationDocument &&
              this.disableIfHidden(
                this.unitControls.registrationUnit.controls._sitePlanInstallationDocument,
                form.controls.sitePlanInstallationDocument
              );
          },
        },
      },
    },
    manufacturer: {
      visible: false,
      reset: form => form.controls.manufacturer.setValue(null),
      add: form => form.addControl('manufacturer', this.fb.control(null, [Validators.required])),
      toggle: (form: FormGroup, value: DeviceUnitFormModel) => {
        this.unitControls.manufacturer.visible = this.ccdType !== DrrCcdTypes.mainsInterference;
        this.disableIfHidden(this.unitControls.manufacturer, form.controls.manufacturer);
        if (this.unitControls.manufacturer.visible) {
          this.toggleUnavailable(form.controls.manufacturer as FormControl, value?.unavailable);
        }
      },
    },
    designationType: {
      visible: false,
      reset: form => form.controls.designationType.setValue(null),
      add: form => form.addControl('designationType', this.fb.control(null, [Validators.required])),
      toggle: (form: FormGroup, value: DeviceUnitFormModel) => {
        this.unitControls.designationType.visible = this.ccdType !== DrrCcdTypes.mainsInterference;
        this.disableIfHidden(this.unitControls.designationType, form.controls.designationType);
        if (this.unitControls.designationType.visible) {
          this.toggleUnavailable(form.controls.designationType as FormControl, value?.unavailable);
        }
      },
    },
    electricalPower: {
      visible: false,
      reset: form => form.controls.electricalPower.setValue(null),
      add: form => form.addControl('electricalPower', this.fb.control(null, [Validators.required])),
      toggle: (form: FormGroup, value: DeviceUnitFormModel) => {
        this.unitControls.electricalPower.visible = this.ccdType !== DrrCcdTypes.mainsInterference;
        this.disableIfHidden(this.unitControls.electricalPower, form.controls.electricalPower);
        if (this.unitControls.electricalPower.visible) {
          this.toggleUnavailable(form.controls.electricalPower as FormControl, value?.unavailable);
        }
      },
    },
    powerOfAdditionalHeater: {
      visible: false,
      reset: form => form.controls.powerOfAdditionalHeater.setValue(null),
      add: form => form.addControl('powerOfAdditionalHeater', this.fb.control(null, [Validators.required])),
      toggle: (form: FormGroup, value: DeviceUnitFormModel) => {
        this.unitControls.powerOfAdditionalHeater.visible =
          [DrrCcdTypes.heatPump, DrrCcdTypes.airConditioner].includes(this.ccdType) && value?.hasAdditionalHeating;
        this.disableIfHidden(this.unitControls.powerOfAdditionalHeater, form.controls.powerOfAdditionalHeater);
        if (this.unitControls.powerOfAdditionalHeater.visible) {
          this.toggleUnavailable(form.controls.powerOfAdditionalHeater as FormControl, value?.unavailable);
        }
      },
    },
    hasAdditionalHeating: {
      visible: false,
      reset: form => form.controls.hasAdditionalHeating.setValue(null),
      add: form => form.addControl('hasAdditionalHeating', this.fb.control(null, [Validators.required])),
      toggle: (form: FormGroup, value: DeviceUnitFormModel) => {
        this.unitControls.hasAdditionalHeating.visible = [DrrCcdTypes.heatPump, DrrCcdTypes.airConditioner].includes(this.ccdType);
        this.disableIfHidden(this.unitControls.hasAdditionalHeating, form.controls.hasAdditionalHeating);
        if (this.unitControls.hasAdditionalHeating.visible) {
          this.toggleUnavailable(form.controls.hasAdditionalHeating as FormControl, value?.unavailable);
        }
      },
    },
    unavailable: {
      visible: false,
      reset: form => form.controls.unavailable.setValue(null),
      label: () =>
        ['heat_pump'].includes(this.ccdType)
          ? this.translation.translateString('drr-ccd-devices.inner-unit-unavailable-lbl')
          : this.translation.translateString('drr-ccd-devices.outer-unit-unavailable-lbl'),
      add: form => form.addControl('unavailable', this.fb.control(null, [])),
      toggle: (form: FormGroup, value: DeviceUnitFormModel) => {
        this.unitControls.unavailable.visible = [DrrCcdTypes.heatPump, DrrCcdTypes.airConditioner].includes(this.ccdType);
        this.disableIfHidden(this.unitControls.unavailable, form.controls.unavailable);
      },
    },
    loadManagementAvailable: {
      add: form => form.addControl('loadManagementAvailable', this.fb.control(null, [Validators.required])),
      visible: false,
      reset: form => form.controls.loadManagementAvailable.setValue(null),
      toggle: (form: FormGroup, value: DeviceUnitFormModel) => {
        this.unitControls.loadManagementAvailable.visible = [DrrCcdTypes.wallBox, DrrCcdTypes.chargingDevice].includes(this.ccdType);
        this.disableIfHidden(this.unitControls.loadManagementAvailable, form.controls.loadManagementAvailable);
        if (this.unitControls.loadManagementAvailable.visible) {
          this.toggleUnavailable(form.controls.loadManagementAvailable as FormControl, value?.unavailable);
        }
      },
    },
    storageNotInParallelMode: {
      add: form => form.addControl('storageNotInParallelMode', this.fb.control(null, [Validators.required])),
      visible: false,
      reset: form => form.controls.storageNotInParallelMode.setValue(null),
      toggle: (form: FormGroup, value: DeviceUnitFormModel) => {
        this.unitControls.storageNotInParallelMode.visible = [DrrCcdTypes.storage].includes(this.ccdType);
        this.disableIfHidden(this.unitControls.storageNotInParallelMode, form.controls.storageNotInParallelMode);
        if (this.unitControls.storageNotInParallelMode.visible) {
          this.toggleUnavailable(form.controls.storageNotInParallelMode as FormControl, value?.unavailable);
        }
      },
    },
    loadManagementType: {
      visible: false,
      reset: form => form.controls.loadManagementType.setValue(null),
      add: form => form.addControl('loadManagementType', this.fb.control(null, [Validators.required])),
      toggle: (form: FormGroup, value: DeviceUnitFormModel) => {
        this.unitControls.loadManagementType.visible =
          [DrrCcdTypes.wallBox, DrrCcdTypes.chargingDevice].includes(this.ccdType) && value?.loadManagementAvailable;
        this.disableIfHidden(this.unitControls.loadManagementType, form.controls.loadManagementType);
        if (this.unitControls.loadManagementType.visible) {
          this.toggleUnavailable(form.controls.loadManagementType as FormControl, value?.unavailable);
        }
      },
    },
    couplingType: {
      visible: false,
      reset: form => form.controls.couplingType.setValue(null),
      add: form => form.addControl('couplingType', this.fb.control(null, [Validators.required])),
      toggle: (form: FormGroup, value: DeviceUnitFormModel) => {
        this.unitControls.couplingType.visible = [DrrCcdTypes.storage].includes(this.ccdType);
        this.disableIfHidden(this.unitControls.couplingType, form.controls.couplingType);
        if (this.unitControls.couplingType.visible) {
          this.toggleUnavailable(form.controls.couplingType as FormControl, value?.unavailable);
        }
      },
    },
    subUnits: {
      visible: false,
      reset: form => {
        for (const subUnit of (form.controls.subUnits as FormArray).controls) {
          this.resetUnitControls(this.subUnitControls, subUnit as FormGroup);
        }
      },
      add: form => form.addControl('subUnits', this.fb.array([])),
      toggle: (form: FormGroup, value: DeviceUnitFormModel) => {
        const isSubUnitsVisible = [DrrCcdTypes.heatPump, DrrCcdTypes.airConditioner].includes(this.ccdType);
        this.unitControls.subUnits.visible = isSubUnitsVisible;
        if (isSubUnitsVisible && !form.getRawValue().subUnits.length) {
          this.addSubUnit(form);
        } else if (!isSubUnitsVisible && form.getRawValue().subUnits.length) {
          (form.controls.subUnits as FormArray).clear();
        }

        for (const subUnit of (form.controls.subUnits as FormArray).controls) {
          this.toggleAllControls(this.subUnitControls, subUnit as FormGroup, subUnit.value);
        }
      },
    },
    _dataSheet: {
      add: _ => {},
      default: createEmptyFileInputFormValue('drr_ccd_data_sheet'),
      visible: false,
      toggle: (form: FormGroup, value: DeviceUnitFormModel) => {
        this.unitControls._dataSheet.visible = [
          DrrCcdTypes.heatPump,
          DrrCcdTypes.airConditioner,
          DrrCcdTypes.wallBox,
          DrrCcdTypes.chargingDevice,
          DrrCcdTypes.storage,
        ].includes(this.ccdType);
        this.removeIfHiddenFileUpload(this.unitControls._dataSheet, form, 'dataSheet');
      },
    },
    _dataSheetWr: {
      add: _ => {},
      default: createEmptyFileInputFormValue('drr_ccd_data_sheet_wr'),
      visible: false,
      toggle: (form: FormGroup, value: DeviceUnitFormModel) => {
        this.unitControls._dataSheetWr.visible = value?.couplingType === 'ac';
        this.removeIfHiddenFileUpload(this.unitControls._dataSheetWr, form, 'dataSheetWr');
      },
    },
    _circuitDiagramDocument: {
      add: _ => {},
      default: createEmptyFileInputFormValue('drr_ccd_circuit_diagram'),
      visible: false,
      toggle: (form: FormGroup, value: DeviceUnitFormModel) => {
        form.controls.circuitDiagramDocument &&
          this.disableIfHidden(this.unitControls._circuitDiagramDocument, form.controls.circuitDiagramDocument);
      },
    },
  };

  subUnitControls = {
    id: {
      add: form => form.addControl('id', this.fb.control(null)),
      visible: false,
      toggle: _ => {},
    },
    manufacturer: {
      visible: false,
      reset: form => form.controls.manufacturer.setValue(null),
      add: form => form.addControl('manufacturer', this.fb.control(null, [Validators.required])),
      toggle: (form: FormGroup, value: DeviceUnitFormModel) => {
        this.subUnitControls.manufacturer.visible = true;
        this.disableIfHidden(this.subUnitControls.manufacturer, form.controls.manufacturer);
        if (this.subUnitControls.manufacturer.visible) {
          this.toggleUnavailable(form.controls.manufacturer as FormControl, value?.unavailable);
        }
      },
    },
    designationType: {
      visible: false,
      reset: form => form.controls.designationType.setValue(null),
      add: form => form.addControl('designationType', this.fb.control(null, [Validators.required])),
      toggle: (form: FormGroup, value: DeviceUnitFormModel) => {
        this.subUnitControls.designationType.visible = true;
        this.disableIfHidden(this.subUnitControls.designationType, form.controls.designationType);
        if (this.subUnitControls.designationType.visible) {
          this.toggleUnavailable(form.controls.designationType as FormControl, value?.unavailable);
        }
      },
    },
    electricalPower: {
      visible: false,
      reset: form => form.controls.electricalPower.setValue(null),
      add: form => form.addControl('electricalPower', this.fb.control(null, [Validators.required])),
      toggle: (form: FormGroup, value: DeviceUnitFormModel) => {
        this.subUnitControls.electricalPower.visible = true;
        this.disableIfHidden(this.subUnitControls.electricalPower, form.controls.electricalPower);
        if (this.subUnitControls.electricalPower.visible) {
          this.toggleUnavailable(form.controls.electricalPower as FormControl, value?.unavailable);
        }
      },
    },
    powerOfAdditionalHeater: {
      visible: false,
      reset: form => form.controls.powerOfAdditionalHeater.setValue(null),
      add: form => form.addControl('powerOfAdditionalHeater', this.fb.control(null, [Validators.required])),
      toggle: (form: FormGroup, value: DeviceUnitFormModel) => {
        this.subUnitControls.powerOfAdditionalHeater.visible =
          [DrrCcdTypes.heatPump, DrrCcdTypes.airConditioner].includes(this.ccdType) && form.controls.hasAdditionalHeating?.value;
        this.disableIfHidden(this.subUnitControls.powerOfAdditionalHeater, form.controls.powerOfAdditionalHeater);
        if (this.subUnitControls.powerOfAdditionalHeater.visible) {
          this.toggleUnavailable(form.controls.powerOfAdditionalHeater as FormControl, value?.unavailable);
        }
      },
    },
    hasAdditionalHeating: {
      visible: false,
      reset: form => form.controls.hasAdditionalHeating.setValue(null),
      add: form => form.addControl('hasAdditionalHeating', this.fb.control(null, [Validators.required])),
      toggle: (form: FormGroup, value: DeviceUnitFormModel) => {
        this.subUnitControls.hasAdditionalHeating.visible = [DrrCcdTypes.heatPump, DrrCcdTypes.airConditioner].includes(this.ccdType);
        this.disableIfHidden(this.subUnitControls.hasAdditionalHeating, form.controls.hasAdditionalHeating);
        if (this.subUnitControls.hasAdditionalHeating.visible) {
          this.toggleUnavailable(form.controls.hasAdditionalHeating as FormControl, value?.unavailable);
        }
      },
    },
    unavailable: {
      visible: false,
      disabled: false,
      reset: form => form.controls.unavailable.setValue(null),
      label: () =>
        ['heat_pump'].includes(this.ccdType)
          ? this.translation.translateString('drr-ccd-devices.outer-unit-unavailable-lbl')
          : this.translation.translateString('drr-ccd-devices.inner-unit-unavailable-lbl'),
      add: form => form.addControl('unavailable', this.fb.control(null, [])),
      toggle: (form: FormGroup, value: DeviceUnitFormModel) => {
        this.subUnitControls.unavailable.visible = true;
        this.disableIfHidden(this.subUnitControls.unavailable, form.controls.unavailable);
      },
    },
    _dataSheet: {
      add: _ => {},
      default: createEmptyFileInputFormValue('drr_ccd_data_sheet'),
      visible: false,
      toggle: (form: FormGroup, value: DeviceUnitFormModel) => {
        this.subUnitControls._dataSheet.visible = [
          DrrCcdTypes.heatPump,
          DrrCcdTypes.airConditioner,
          DrrCcdTypes.wallBox,
          DrrCcdTypes.chargingDevice,
          DrrCcdTypes.storage,
        ].includes(this.ccdType);
        this.removeIfHiddenFileUpload(this.subUnitControls._dataSheet, form, 'dataSheet');
      },
    },
  };

  addUnit(unit: DeviceUnitFormModel): FormGroup {
    const unitFormControl = this.fb.group({});
    this.addAllControls(this.unitControls, unitFormControl);
    unitFormControl.addControl('unitTotalPower', this.fb.control(0));
    if (unit) {
      unitFormControl.patchValue(unit);
      unitFormControl.addControl('dataSheetRawValue', this.fb.control(unit.dataSheet));
      unitFormControl.addControl('dataSheetRwRawValue', this.fb.control(unit.dataSheetWr));
      unitFormControl.addControl('circuitDiagramRawValue', this.fb.control(unit.circuitDiagramDocument));
      (unitFormControl.controls.registrationUnit as FormGroup)?.addControl(
        'dataCollectionRawValue',
        this.fb.control(unit.registrationUnit?.dataCollectionDocument)
      );
      (unitFormControl.controls.registrationUnit as FormGroup)?.addControl(
        'sitePlanInstallationRawValue',
        this.fb.control(unit.registrationUnit?.sitePlanInstallationDocument)
      );

      for (const subUnit of unit.subUnits) {
        this.addSubUnit(unitFormControl, subUnit);
      }
    }

    this.toggleAllControls(this.unitControls, unitFormControl, unit);
    this.setDisabledForSubUnits(unit?.unavailable, unitFormControl.controls.subUnits as FormArray);
    this.setDisabledForUnit(unitFormControl);
    unitFormControl.controls.unavailable.valueChanges.subscribe(value => {
      this.toggleAllControls(
        {
          manufacturer: this.unitControls.manufacturer,
          designationType: this.unitControls.designationType,
          electricalPower: this.unitControls.electricalPower,
          powerOfAdditionalHeater: this.unitControls.powerOfAdditionalHeater,
          hasAdditionalHeating: this.unitControls.hasAdditionalHeating,
          loadManagementAvailable: this.unitControls.loadManagementAvailable,
          storageNotInParallelMode: this.unitControls.storageNotInParallelMode,
          loadManagementType: this.unitControls.loadManagementType,
          couplingType: this.unitControls.couplingType,
        },
        unitFormControl,
        { unavailable: value }
      );
      this.setDisabledForSubUnits(value, unitFormControl.controls.subUnits as FormArray);
    });
    unitFormControl.controls.hasAdditionalHeating.valueChanges.subscribe(value => {
      this.toggleAllControls({ powerOfAdditionalHeater: this.unitControls.powerOfAdditionalHeater }, unitFormControl, {
        hasAdditionalHeating: value,
      });
    });
    unitFormControl.controls.couplingType.valueChanges.subscribe(value => {
      this.toggleAllControls({ dataSheetWr: this.unitControls._dataSheetWr }, unitFormControl, {
        couplingType: value,
      });
    });
    unitFormControl.controls.loadManagementAvailable.valueChanges.subscribe(value => {
      this.toggleAllControls(
        {
          loadManagementType: this.unitControls.loadManagementType,
        },
        unitFormControl,
        { loadManagementAvailable: value }
      );
    });
    unitFormControl.valueChanges.subscribe(_ => {
      this.calculateUnitTotalPower(unitFormControl);
    });
    unitFormControl.controls.storageNotInParallelMode.valueChanges.subscribe(value => {
      if (!value) {
        unitFormControl.controls.storageNotInParallelMode.setValue(null, { emitEvent: false });
      }
    });
    unitFormControl.controls.registrationUnit.get('mainsInterferenceDeviceType').valueChanges.subscribe(value => {
      this.toggleAllControls(
        this.networkDisturbancesDataControls,
        unitFormControl.controls.registrationUnit.get('networkDisturbancesData') as FormGroup,
        {
          mainsInterferenceDeviceType: value,
        }
      );
    });
    unitFormControl.controls.registrationUnit.get('networkDisturbancesData.startUpCircuit').valueChanges.subscribe(value => {
      this.toggleAllControls(
        { otherStartupCircuit: this.networkDisturbancesDataControls.otherStartupCircuit },
        unitFormControl.controls.registrationUnit.get('networkDisturbancesData') as FormGroup,
        {
          mainsInterferenceDeviceType: unitFormControl.controls.registrationUnit.get('mainsInterferenceDeviceType').value,
          startUpCircuit: value,
        }
      );
    });

    return unitFormControl;
  }

  addSubUnit(parentUnit: FormGroup, subUnit?: DeviceUnitFormModel) {
    const subUnitFormControl = this.fb.group({});
    this.addAllControls(this.subUnitControls, subUnitFormControl);
    if (subUnit) {
      subUnitFormControl.addControl('dataSheetRawValue', this.fb.control(subUnit.dataSheet));
      subUnitFormControl.patchValue(subUnit);
    }

    this.toggleAllControls(this.subUnitControls, subUnitFormControl, subUnit);
    subUnitFormControl.controls.unavailable.valueChanges.subscribe(value => {
      this.toggleAllControls(
        {
          manufacturer: this.subUnitControls.manufacturer,
          designationType: this.subUnitControls.designationType,
          electricalPower: this.subUnitControls.electricalPower,
          powerOfAdditionalHeater: this.subUnitControls.powerOfAdditionalHeater,
          hasAdditionalHeating: this.subUnitControls.hasAdditionalHeating,
        },
        subUnitFormControl,
        { unavailable: value }
      );
      this.setDisabledForUnit(parentUnit);
    });
    subUnitFormControl.controls.hasAdditionalHeating.valueChanges.subscribe(value => {
      this.toggleAllControls({ powerOfAdditionalHeater: this.subUnitControls.powerOfAdditionalHeater }, subUnitFormControl, {
        hasAdditionalHeating: value,
      });
    });

    if (parentUnit.controls.unavailable.value) {
      subUnitFormControl.controls.unavailable.disable({ emitEvent: false });
    }
    if (parentUnit.controls.subUnits) {
      const subUnits = parentUnit.controls.subUnits as FormArray;
      subUnits.controls.push(subUnitFormControl);
      this.setDisabledForSubUnits(parentUnit.controls.unavailable.value, subUnits);

      subUnitFormControl.valueChanges.subscribe(_ => {
        subUnits.updateValueAndValidity();
      });
    }
  }

  setDisabledForUnit(parentUnit: FormGroup) {
    const subUnits = parentUnit.controls.subUnits as FormArray;
    if (subUnits.controls.some((c: FormGroup) => c.controls.unavailable.value)) {
      parentUnit.controls.unavailable.disable({ emitEvent: false });
    } else {
      parentUnit.controls.unavailable.enable({ emitEvent: false });
    }
  }

  setDisabledForSubUnits(parentUnavailableValue: boolean, subUnits: FormArray) {
    subUnits.controls.forEach((subUnit: FormGroup) => {
      if (parentUnavailableValue || subUnits.controls.length > 1) {
        subUnit.controls.unavailable.setValue(null);
        subUnit.controls.unavailable.disable({ emitEvent: false });
      } else {
        subUnit.controls.unavailable.enable({ emitEvent: false });
      }
    });
  }

  toggleUnavailable(control: FormControl, isUnavailable: boolean) {
    if (isUnavailable) {
      control.disable({ emitEvent: false });
      control.setValue(null);
    } else {
      control.enable({ emitEvent: false });
    }
  }

  disableIfHidden(controlSettings: any, formControl: AbstractControl) {
    if (!controlSettings.visible) {
      formControl.disable({ emitEvent: false });
      formControl.setValue(controlSettings.default ?? null);
    } else if (controlSettings.visible && formControl.disabled) {
      formControl.enable({ emitEvent: false });
    }
  }

  addAllControls(controls: any, form: FormGroup) {
    Object.values(controls).forEach((control: any) => {
      if (control.add) {
        control.add(form);
      }
      if (control.default) {
        form.addControl(control.default.name, this.fb.control(control.default));
      }
    });
  }

  toggleAllControls(controls: any, targetForm: FormGroup, formValue: any) {
    Object.values(controls).forEach((control: any) => {
      if (control.toggle) {
        control.toggle(targetForm, formValue);
      }
    });
  }

  calculateUnitTotalPower(unitFormControl: FormGroup) {
    let totalPower = 0;
    Object.values(this.unitPowerFieldNames).forEach(field => {
      totalPower += Number(unitFormControl.controls[field]?.value ?? 0);
    });

    const subUnits = unitFormControl.controls.subUnits as FormArray;
    subUnits.controls.forEach((unit: FormGroup) => {
      Object.values(this.subUnitPowerFieldNames).forEach(field => {
        totalPower += Number(unit.controls[field]?.value ?? 0);
      });
    });

    unitFormControl.patchValue({ unitTotalPower: totalPower }, { emitEvent: false });
  }

  resetUnitControls(controls: any, targetForm: FormGroup) {
    Object.values(controls).forEach((control: any) => {
      if (control.reset) {
        control.reset(targetForm);
      }
    });
  }

  private removeIfHiddenFileUpload(controlSettings: any, form: FormGroup, controlName: string) {
    const formControl = form.get(controlName) as FormControl;
    if (formControl) {
      if (!controlSettings.visible && formControl.enabled) {
        form.removeControl(controlName);
      } else if (controlSettings.visible && formControl.disabled) {
        formControl.enable();
      }
    }
  }
}
